'use client'
import { ReviewCard } from '@/components'
import { fetchPropertyReviews } from '@/queries'
import { memo, useEffect, useRef, useState } from 'react'
import RatingShowsSkeleton from './RatingShowsSkeleton'

const PAGE_SIZE = 12

const ReviewSection = ({
  id,
  initialReviews = [],
  initialHasMore = true,
  initialTotalCount = null,
}) => {
  const [reviews, setReviews] = useState(initialReviews)
  const [limit, setLimit] = useState(4)
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(initialHasMore)

  const currentPage = useRef(1)
  const totalCount = useRef(initialTotalCount)

  useEffect(() => {
    currentPage.current = 1
    totalCount.current = initialTotalCount
    setReviews(initialReviews)
    setHasMore(initialHasMore)
    setLimit(4)
    setLoading(false)
  }, [id, initialHasMore, initialReviews, initialTotalCount])

  const loadPage = async (page) => {
    try {
      setLoading(true)

      const res = await fetchPropertyReviews(id, page)
      if (!res || !res.data || !res.data.data) {
        console.error('Invalid API response')
        setHasMore(false)
        return
      }

      const data = res.data.data
      const items = Array.isArray(data.review) ? data.review : []
      const total = Number(data.totalCount) || 0

      if (!data.next) {
        setHasMore(false)
      }

      if (totalCount.current === null) {
        totalCount.current = total
      }

      if (items.length === 0) {
        setHasMore(false)
        return
      }

      setReviews((prev) => {
        const merged = [...prev, ...items]

        if (items.length === 0) {
          setHasMore(false)
        }

        return merged
      })
    } catch (error) {
      console.error('API error:', error)
      setHasMore(false)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!id) {
      return
    }

    if (initialReviews.length > 0) {
      return
    }

    loadPage(1)
  }, [id, initialReviews])

  useEffect(() => {
    if (!hasMore || loading) return

    const threshold = currentPage.current * PAGE_SIZE
    if (limit >= threshold) {
      const nextPage = currentPage.current + 1
      currentPage.current = nextPage
      loadPage(nextPage)
    }
  }, [limit, hasMore, loading])

  const handleLoadMore = () => {
    const newLimit = limit + 2
    setLimit(newLimit)

    if (newLimit >= reviews.length && hasMore && !loading) {
      const nextPage = currentPage.current + 1
      currentPage.current = nextPage
      loadPage(nextPage)
    }
  }

  const handleShowLess = () => {
    setLimit(4)
  }

  const showLess =
    !hasMore && limit >= reviews.length && reviews.length > PAGE_SIZE

  if (loading) {
    return <RatingShowsSkeleton />
  }

  return (
    <div className='flex flex-col gap-8 mb-10'>
      <div className='flex flex-wrap gap-6 mt-10 w-full mx-auto md:w-[804px]'>
        {reviews.slice(0, limit).map((review) => (
          <div
            key={`review-${review.property_uid}-${review.source}-${review.date}`}
            className='w-full md:w-[48%]'
          >
            <ReviewCard
              name={review.author}
              date={review.date}
              rating={parseFloat(review.rating)}
              review={review.content}
              avatarUrl={null}
              source={review.source}
            />
          </div>
        ))}
      </div>

      {reviews.length > 4 && (
        <div className='flex w-full mx-auto justify-center md:w-[804px] md:justify-start'>
          <button
            onClick={showLess ? handleShowLess : handleLoadMore}
            disabled={loading}
            aria-label={showLess ? 'Show fewer reviews' : 'Load more reviews'}
            className='w-[168px] h-[37px] rounded-[8px] border border-[#D2D2D2] bg-white font-medium text-[13px] hover:bg-gray-50 hover:border-gray-300'
          >
            {loading
              ? 'Loading...'
              : showLess
                ? 'Show less reviews'
                : 'Load more reviews'}
          </button>
        </div>
      )}
    </div>
  )
}

export default memo(ReviewSection)
